/**
 * Shared axios response-error interceptor for JWT token refresh.
 *
 * Used by both the main `api` instance and `campaignsApi` so that
 * ANY 401 triggers a refresh-and-retry cycle regardless of which
 * axios client issued the request.
 */

interface AuthServiceLike {
  getAccessToken(): string | null
  refreshAccessToken(): Promise<unknown>
  clearTokens(): void
}

let refreshPromise: Promise<void> | null = null

/**
 * Creates a response error handler that can be attached to any axios instance.
 *
 * Uses a module-level promise mutex so that concurrent 401s coalesce
 * into a single refreshAccessToken() call. All waiting requests retry
 * once the shared refresh resolves.
 *
 * @param auth  – auth service (or compatible mock)
 * @param retry – function that re-sends the original request config
 *                (usually `(cfg) => axiosInstance(cfg)`)
 */
export function createResponseErrorHandler(
  auth: AuthServiceLike,
  retry: (config: any) => Promise<any>,
) {
  return async (error: any) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      // Coalesce: if a refresh is already in-flight, wait for it
      if (!refreshPromise) {
        refreshPromise = (auth.refreshAccessToken() as Promise<void>)
          .catch((refreshError) => {
            auth.clearTokens()
            localStorage.removeItem('apiKey')
            localStorage.removeItem('user')
            localStorage.removeItem('loginTimestamp')
            window.location.href = '/login'
            throw refreshError
          })
          .finally(() => {
            refreshPromise = null
          })
      }

      await refreshPromise
      originalRequest.headers['Authorization'] = `Bearer ${auth.getAccessToken()}`
      return retry(originalRequest)
    }

    return Promise.reject(error)
  }
}

/** Reset module state — only for tests */
export function _resetRefreshState() {
  refreshPromise = null
}
